import { useEffect, useState } from 'react';
import { ExternalLink, Hash, Images, UserRound } from 'lucide-react';
import Drawer from '../../components/Drawer';
import Button from '../../components/Button';
import AuthMessage from '../../components/AuthMessage';
import StatusBadge from '../../components/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import {
  TICKET_ORDER_CHANNEL,
  TICKET_ORDER_CHANNEL_LABELS,
  TICKET_RESOLUTION,
  TICKET_RESOLUTION_LABELS,
  SUPPORT_ERRORS,
  parseSubscriptionIssueMessage,
  formatTicketListTitle,
  suggestTicketCreditAmount,
} from '../../api/support';
import form from '../../styles/form.module.css';
import styles from './TicketDrawer.module.css';

const STATUS_OPTIONS = ['OPEN', 'IN_PROGRESS', 'CLOSED'];

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function TicketDrawer({ ticket, onClose, onRespond }) {
  const { user } = useAuth();
  const [status, setStatus] = useState('OPEN');
  const [opsNote, setOpsNote] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const parsedIssue = ticket ? parseSubscriptionIssueMessage(ticket.message) : null;
  const isSubs = ticket?.orderChannel === TICKET_ORDER_CHANNEL.SUBS;
  const alreadyResolved = Boolean(ticket?.resolution);
  // Subscription tickets: dispose + credit only. Grocery: status + note only.
  const canDispose = isSubs && Boolean(ticket?.orderId) && !alreadyResolved;

  useEffect(() => {
    if (!ticket) return;
    setStatus(ticket.status || 'OPEN');
    setOpsNote(ticket.opsNote || '');
    setCreditAmount(
      ticket.creditAmount != null && ticket.creditAmount > 0
        ? String(ticket.creditAmount)
        : '',
    );
    setError('');

    let cancelled = false;
    if (canDispose && !ticket.creditAmount) {
      suggestTicketCreditAmount(ticket, user?.idToken).then((amount) => {
        if (cancelled || amount == null) return;
        setCreditAmount((prev) => (prev ? prev : String(amount)));
      });
    }
    return () => { cancelled = true; };
  }, [ticket, user?.idToken, canDispose]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const resolution = canDispose ? TICKET_RESOLUTION.DISPOSE : undefined;
      if (resolution === TICKET_RESOLUTION.DISPOSE) {
        const amount = Number(creditAmount);
        if (!Number.isFinite(amount) || amount <= 0) {
          throw Object.assign(new Error('invalid_credit_amount'), {
            code: 'invalid_credit_amount',
          });
        }
      }
      await onRespond(ticket, {
        // Dispose path forces CLOSED in the API layer; don't send a stale OPEN.
        status: resolution === TICKET_RESOLUTION.DISPOSE ? 'CLOSED' : status,
        opsNote: opsNote.trim(),
        resolution,
        creditAmount: resolution === TICKET_RESOLUTION.DISPOSE ? creditAmount : undefined,
      });
    } catch (err) {
      const code = err?.code || err?.message;
      setError(
        SUPPORT_ERRORS[code]
        || err?.detail
        || err?.message
        || 'Could not update this ticket. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const primaryLabel = (() => {
    if (submitting) return 'Saving…';
    if (canDispose) {
      return creditAmount ? `Dispose & credit ₹${creditAmount}` : 'Dispose & credit';
    }
    return 'Update ticket';
  })();

  return (
    <Drawer
      open={!!ticket}
      onClose={handleClose}
      title={ticket ? formatTicketListTitle(ticket) : 'Support ticket'}
      subtitle={ticket ? `Ticket #${ticket.ticketId}` : undefined}
      width={460}
      footer={(
        <>
          <Button variant="ghost" onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {primaryLabel}
          </Button>
        </>
      )}
    >
      {ticket && (
        <form onSubmit={handleSubmit}>
          <AuthMessage>{error}</AuthMessage>

          <div className={styles.metaRow}>
            <div className={styles.metaItem}>
              <UserRound size={13} />
              <span title={ticket.customerSub}>{ticket.customerSub}</span>
            </div>
            {ticket.orderId && (
              <div className={styles.metaItem}>
                <Hash size={13} />
                <span>{ticket.orderId}</span>
                {isSubs && (
                  <span className={styles.channelBadgeSubs}>
                    {TICKET_ORDER_CHANNEL_LABELS[TICKET_ORDER_CHANNEL.SUBS]}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className={styles.timestamps}>
            <span>Raised {formatDate(ticket.createdAt)}</span>
            <span>Updated {formatDate(ticket.updatedAt)}</span>
          </div>

          {alreadyResolved && (
            <div className={styles.resolvedBanner}>
              <strong>{ticket.resolutionLabel || TICKET_RESOLUTION_LABELS[ticket.resolution]}</strong>
              {ticket.resolution === TICKET_RESOLUTION.DISPOSE && (
                ticket.creditAmount != null
                  ? <> · ₹{ticket.creditAmount} credited to wallet</>
                  : ticket.walletCredited
                    ? <> · wallet credit applied</>
                    : null
              )}
              {ticket.returnIds?.length > 0 && (
                <> · RMA {ticket.returnIds.join(', ')}</>
              )}
              {ticket.resolvedAt && (
                <span className={styles.resolvedWhen}> · {formatDate(ticket.resolvedAt)}</span>
              )}
            </div>
          )}

          <div className={styles.messageBox}>
            <p className={styles.messageLabel}>
              {parsedIssue ? 'Issue details' : 'Customer message'}
            </p>
            {parsedIssue ? (
              <dl className={styles.issueFields}>
                <div className={styles.issueField}>
                  <dt>Product</dt>
                  <dd>{parsedIssue.productName}</dd>
                </div>
                <div className={styles.issueField}>
                  <dt>Reason</dt>
                  <dd>{parsedIssue.reasonLabel}</dd>
                </div>
                <div className={styles.issueField}>
                  <dt>Order</dt>
                  <dd>{parsedIssue.orderId || ticket.orderId || '—'}</dd>
                </div>
                {parsedIssue.lineValue && (
                  <div className={styles.issueField}>
                    <dt>Line value</dt>
                    <dd>{parsedIssue.lineValue}</dd>
                  </div>
                )}
                {parsedIssue.customerNote && (
                  <div className={styles.issueField}>
                    <dt>Customer note</dt>
                    <dd className={styles.issueNote}>{parsedIssue.customerNote}</dd>
                  </div>
                )}
              </dl>
            ) : (
              <p className={styles.messageText}>{ticket.message}</p>
            )}
          </div>

          {Array.isArray(ticket.imageUrls) && ticket.imageUrls.length > 0 && (
            <div className={styles.photosBlock}>
              <p className={styles.messageLabel}>
                <Images size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Customer photos ({ticket.imageUrls.length})
              </p>
              <div className={styles.photoGrid}>
                {ticket.imageUrls.map((url, index) => (
                  <a
                    key={`evidence-${index}`}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.photoThumbWrap}
                  >
                    <img src={url} alt="Customer evidence" className={styles.photoThumb} />
                    <span className={styles.photoThumbLabel}>
                      <ExternalLink size={10} /> Open
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {canDispose && (
            <div className={styles.decisionBlock}>
              <p className={styles.decisionTitle}>Dispose + credit</p>
              <p className={styles.decisionHint}>
                Subscription issue — add store credit now. The customer may dispose of the item at home.
              </p>
              <label className={form.field}>
                <span className={form.label}>Wallet credit (₹)</span>
                <input
                  className={form.input}
                  type="number"
                  min="1"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="e.g. 35"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  disabled={submitting}
                  required
                />
                <span className={form.hint}>
                  Customer is notified and the ticket closes when you save.
                </span>
              </label>
            </div>
          )}

          {isSubs && !ticket.orderId && (
            <p className={styles.handoffHint}>
              No order is linked on this subscription ticket, so dispose/credit is unavailable.
              Update status and note only.
            </p>
          )}

          {!isSubs && (
            <p className={styles.handoffHint}>
              Grocery tickets use status and note here. Pickup and wallet credit run through{' '}
              <strong>Return &amp; Exchange</strong>.
            </p>
          )}

          {!canDispose && (
            <label className={form.field}>
              <span className={form.label}>Status</span>
              <select
                className={form.select}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={submitting}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s.replace('_', ' ')}</option>
                ))}
              </select>
            </label>
          )}

          <label className={form.field}>
            <span className={form.label}>Note to customer</span>
            <textarea
              className={form.textarea}
              placeholder={
                canDispose
                  ? 'e.g. ₹35 store credit added — you may dispose of the item safely.'
                  : 'e.g. We are looking into this and will update you shortly.'
              }
              value={opsNote}
              onChange={(e) => setOpsNote(e.target.value)}
              rows={3}
              disabled={submitting}
            />
            <span className={form.hint}>Optional. Sent as a push notification when you save.</span>
          </label>

          <div className={styles.currentStatusRow}>
            <span className={form.hint}>Current status:</span>
            <StatusBadge status={ticket.status} size="sm" />
          </div>
        </form>
      )}
    </Drawer>
  );
}
